import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Document } from "domain/entities/document";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { Logger } from "infra/logger/logger";
import documentRepository from "infra/repositories/document-repository";

export const useDocumentByFilter = (
  filter: DocumentInputFilter,
  options?: { enabled: boolean }
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["documents", filter],
    queryFn: async () => {
      try {
        if (
          !filter.employeeId &&
          !filter.timesheetId &&
          !filter.invoiceMonth &&
          !filter.invoiceYear &&
          !filter.documentTypeId
        )
          return;
        const result = await documentRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
    enabled: options?.enabled ?? true,
  });
};

export const useCreateDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Document) => {
      const result = await documentRepository.create(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [
          "documents",
          { employeeId: data.data?.employee.id } as DocumentInputFilter,
        ],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação do funcionário");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};

export const useCreateCPSDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Document) => {
      const result = await documentRepository.createCPS(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [
          "documents",
          { employeeId: data.data?.employee.id } as DocumentInputFilter,
        ],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação do documento");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};

export const useDeleteCPSDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();
  let employeeId: string = "";

  return useMutation({
    mutationFn: async (data: Document) => {
      if (!data.id) throw new Error("It is missing Document ID");
      employeeId = data.employee.id;
      const result = await documentRepository.deleteCPS(data.id);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [
          "documents",
          { employeeId: employeeId } as DocumentInputFilter,
        ],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};

export const useDeleteDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();
  let employeeId: string = "";

  return useMutation({
    mutationFn: async (data: Document) => {
      if (!data.id) throw new Error("It is missing Document ID");
      employeeId = data.employee.id;
      const result = await documentRepository.delete(data.id);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [
          "documents",
          { employeeId: employeeId } as DocumentInputFilter,
        ],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};
